// @ts-strict-ignore
import type { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import type { DateFilterConfig as InstanceDateFilterConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/date-filter-config'
import type { FilterConfig as InstanceFilterConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/filters-config'
import type { RealtimeFilterConfig as InstanceRealtimeFilterConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/realtime-filter-config'
import type { RegularFilterConfig as InstanceRegularFilterConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-config'
import type { RegularFilterValueConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-value-config'
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { Store } from 'vuex'
import type {
  AppRoute,
  ContextState,
  DateFilter,
  DateFilterConfig,
  DateFilterSelectedValues,
  DateRangeFilter,
  DateRangeFilterConfig,
  DateRangeFilterSelectedValues,
  Dictionary,
  DynamicDateFilterConfig,
  DynamicDateRangeFilterConfig,
  DynamicFilterConfig,
  DynamicRealtimeFilterConfig,
  DynamicRegularFilterConfig,
  Filter,
  FilterConfig,
  FilterSelectedValues,
  FiltersMessages,
  KeyOfContextToolState,
  PivotableToolConfig,
  RealtimeFilter,
  RealtimeFilterConfig,
  RealtimeFilterSelectedValues,
  RegularFilter,
  RegularFilterConfig,
  RegularFilterSelectedValues,
  RouteFiltersConfig,
} from '../models'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'
import { FeatureIdEnum, FilterIdEnum, FilterTypeEnum } from '../enums'
import { capitalizeSentence } from '../filters'
import { ROUTER_CONSTS } from '../router/router.consts'
import { ENABLED_FILTER_CONFIG } from '../router/routes.filters'

import { TOOLING_CONSTS } from '../store/tooling/tooling.consts'

export type FindFilterConfigCriteria = IdCriteria | TaggingIdCriteria | ToolingIdCriteria

export type FindFilterValueConfigCriteria = IdCriteria | TaggingIdCriteria

interface IdCriteria {
  id: string
}

interface TaggingIdCriteria {
  taggingId: string
}

interface ToolingIdCriteria {
  toolingId: string
}

export interface GetFiltersFromContextAndRouteOptions {
  persistedSelectedFiltersValues?: Dictionary<FilterSelectedValues>
  toolStateKey?: KeyOfContextToolState
}

export const ALL_FILTERS_CODE = '*'

export class FiltersUtils {
  static filterBySection(
    filters: Record<string, Filter>,
    section: string,
    featureId?: FeatureIdEnum | string,
  ): Record<string, Filter> {
    return Object.values(filters).reduce<Record<string, Filter>>((result, filter) => {
      const onlyAvailableIn = (filter.config as RegularFilterConfig).onlyAvailableIn ?? []
      if (
        !onlyAvailableIn.length ||
        onlyAvailableIn.includes(section) ||
        (featureId && onlyAvailableIn.includes(featureId))
      ) {
        result[filter.config.id] = filter
      }
      return result
    }, {})
  }

  static filterByType(
    filters: Record<string, Filter>,
    type: FilterTypeEnum,
  ): Record<string, Filter> {
    return Object.fromEntries(
      Object.values(filters)
        .filter(filter => filter.config.type === type)
        .map(filter => [filter.config.id, filter]),
    )
  }

  static updateFilterConfig<FilterType extends Filter>(
    filter: FilterType,
    newConfig: Partial<FilterType['config']>,
  ): FilterType {
    return {
      ...filter,
      config: {
        ...filter.config,
        ...newConfig,
      },
    }
  }

  static updateRegularFilterSelectedValues(
    filter: RegularFilter,
    selectedValues: RegularFilter['selectedValues'],
  ): RegularFilter {
    return {
      ...filter,
      selectedValues,
    }
  }

  static updateFilters<FilterType extends Filter>(
    filters: Record<string, FilterType>,
    updateFilterFn: (filter: FilterType) => FilterType,
  ): Record<string, FilterType> {
    return Object.values(filters).reduce(
      (result: Record<string, FilterType>, filter: FilterType) => {
        result[filter.config.id] = updateFilterFn(filter)
        return result
      },
      {},
    )
  }

  static findConfig(configs: FilterConfig[], criteria: FindFilterConfigCriteria): FilterConfig {
    const id = Object.values(criteria)[0]
    return configs.find((config: FilterConfig) => {
      if ((criteria as IdCriteria).id) {
        return config.id === id
      }
      if (config.type === FilterTypeEnum.REGULAR) {
        const regularConfig = config as RegularFilterConfig
        if ((criteria as TaggingIdCriteria).taggingId) {
          return (regularConfig.taggingId ?? config.id) === id
        }
        if ((criteria as ToolingIdCriteria).toolingId) {
          return (regularConfig.toolingId ?? config.id) === id
        }
      }
      return false
    })
  }

  static findValueConfig(
    valueConfigs: RegularFilterValueConfig[],
    criteria: FindFilterValueConfigCriteria,
  ): RegularFilterValueConfig {
    const id: string = Object.values(criteria)[0]
    return valueConfigs.find((valueConfig: RegularFilterValueConfig) => {
      if ((criteria as IdCriteria).id) {
        return valueConfig.id === id
      }
      if ((criteria as TaggingIdCriteria).taggingId) {
        return (valueConfig.taggingId ?? valueConfig.id).toLowerCase() === id.toLowerCase()
      }
    })
  }

  static getEncodedId(config: RegularFilterConfig): string {
    return config.toolingId ?? config.id
  }

  static encodeValue(config: RegularFilterConfig, value: string): string {
    const valueConfig = FiltersUtils.findValueConfig(config.values, { id: value })
    return valueConfig?.taggingId ?? value
  }

  static decodeValue(config: RegularFilterConfig, value: string): string {
    const valueConfig = FiltersUtils.findValueConfig(config.values, { taggingId: value })
    return valueConfig?.id ?? value
  }

  static getLabel(config: FilterConfig, messages: FiltersMessages): string {
    return config.prettyName ?? messages.names[config.id] ?? capitalizeSentence(config.id)
  }

  static getValueLabel(
    config: RegularFilterConfig,
    valueConfig: RegularFilterValueConfig,
    messages: FiltersMessages,
  ): string {
    return valueConfig.prettyName ?? messages.values[config.id]?.[valueConfig.id] ?? valueConfig.id
  }

  static async getFiltersFromContextAndRoute(
    context: ContextState,
    route: AppRoute,
    instanceConfig: Instance,
    options?: GetFiltersFromContextAndRouteOptions,
  ): Promise<Dictionary<Filter>> {
    const filters: Dictionary<Filter> = {}
    const filtersConfig = await FiltersUtils.getFiltersConfigFromRoute(route, instanceConfig)
    Object.values(filtersConfig).forEach((filterConfig: FilterConfig) => {
      filters[filterConfig.id] = {
        config: filterConfig,
        selectedValues: FiltersUtils.getFilterSelectedValues(context, route, filterConfig, options),
      }
    })
    return FiltersUtils.setFiltersToBeValid(filters)
  }

  static updateRouteFiltersWithContext(route: AppRoute, context: ContextState): AppRoute {
    Object.values(context.filters).forEach((filter: Filter) => {
      const filterUrlValues = {
        [FilterTypeEnum.DATE]: () => FiltersUtils.getDateFilterUrlValues(filter as DateFilter),
        [FilterTypeEnum.DATE_RANGE]: () =>
          FiltersUtils.getDateRangeFilterUrlValues(filter as DateRangeFilter),
        [FilterTypeEnum.REALTIME]: () =>
          FiltersUtils.getRealtimeFilterUrlValues(filter as RealtimeFilter),
        [FilterTypeEnum.REGULAR]: () =>
          FiltersUtils.getRegularFilterUrlValues(filter as RegularFilter),
      }[filter.config.type]()
      const queryFilterId = `${ROUTER_CONSTS.query.filterPrefix}${filter.config.id}`
      if (filterUrlValues) {
        route.query[queryFilterId] = filterUrlValues
      } else {
        delete route.query[queryFilterId]
      }
    })
    return route
  }

  static getClientFilters(filters: Dictionary<Filter>, instanceConfig: Instance): Dictionary {
    return Object.values(filters).reduce((clientFilters: Dictionary, filter: Filter) => {
      if (filter.config.type === FilterTypeEnum.REGULAR) {
        const filterConfig = filter.config as RegularFilterConfig
        const selectedValues = filter.selectedValues as RegularFilterSelectedValues
        if (selectedValues.length) {
          const encodedSelectedValues = selectedValues.map(selectedValue => {
            const selectedValueConfig = filterConfig.values.find(
              valueConfig => valueConfig.id === selectedValue,
            )
            return selectedValueConfig.taggingId || selectedValueConfig.id
          })
          clientFilters[filterConfig.toolingId || filterConfig.id] =
            !instanceConfig.allowMultipleValuesInSearchFilters
              ? encodedSelectedValues[0]
              : encodedSelectedValues
        }
      }
      return clientFilters
    }, {})
  }

  private static async getFiltersConfigFromRoute(
    route: AppRoute,
    instanceConfig: Instance,
  ): Promise<Dictionary<FilterConfig>> {
    const filtersConfig: Dictionary<FilterConfig> = {}

    const routeFiltersConfig = route.meta.filters
      ? await route.meta.filters(instanceConfig)
      : undefined
    Object.values(instanceConfig.filters).forEach((instanceFilterConfig: InstanceFilterConfig) => {
      const filterConfig = FiltersUtils.mergeInstanceAndRouteFilterConfig(
        instanceFilterConfig,
        routeFiltersConfig,
      )
      filtersConfig[filterConfig.id] = filterConfig
    })

    if (!!routeFiltersConfig && !!routeFiltersConfig.extra) {
      Object.values(routeFiltersConfig.extra).forEach((extraFilterConfig: FilterConfig) => {
        filtersConfig[extraFilterConfig.id] = {
          ...extraFilterConfig,
          enabled: true,
        }
      })
    }

    return filtersConfig
  }

  static clearRegularFiltersSelectedAll(filters: Dictionary<Filter>): Dictionary<Filter> {
    const newFilters = {}
    const filtersClean = Object.values(filters).filter(
      (filter: Filter) =>
        filter.config.type !== FilterTypeEnum.REGULAR ||
        (filter.config.type === FilterTypeEnum.REGULAR &&
          ((filter as RegularFilter).config.values.length === 1 ||
            (filter as RegularFilter).config.values.length !==
              (filter as RegularFilter).selectedValues.length)),
    )
    filtersClean.forEach((filter: Filter) => (newFilters[filter.config.id] = filter))
    return newFilters
  }

  private static mergeInstanceAndRouteFilterConfig(
    instanceFilterConfig: InstanceFilterConfig,
    routeFiltersConfig: RouteFiltersConfig,
  ): FilterConfig {
    const routeFilterConfig =
      !!routeFiltersConfig && !!routeFiltersConfig.config
        ? routeFiltersConfig.config[instanceFilterConfig.id]
        : undefined
    const filterConfig = {
      [FilterTypeEnum.DATE]: () =>
        FiltersUtils.mergeInstanceAndRouteDateFilterConfig(
          instanceFilterConfig as InstanceDateFilterConfig,
          routeFilterConfig as DynamicDateFilterConfig,
        ),
      [FilterTypeEnum.DATE_RANGE]: () =>
        FiltersUtils.mergeInstanceAndRouteDateRangeFilterConfig(
          instanceFilterConfig as InstanceDateFilterConfig,
          routeFilterConfig as DynamicDateRangeFilterConfig,
        ),
      [FilterTypeEnum.REALTIME]: () =>
        FiltersUtils.mergeInstanceAndRouteRealtimeFilterConfig(
          instanceFilterConfig as InstanceRealtimeFilterConfig,
          routeFilterConfig as DynamicRealtimeFilterConfig,
        ),
      [FilterTypeEnum.REGULAR]: () =>
        FiltersUtils.mergeInstanceAndRouteRegularFilterConfig(
          instanceFilterConfig as InstanceRegularFilterConfig,
          routeFilterConfig as DynamicRegularFilterConfig,
        ),
    }[instanceFilterConfig.type]()

    if (filterConfig.type === FilterTypeEnum.REGULAR) {
      const regularFilterConfig = filterConfig as RegularFilterConfig
      if (routeFiltersConfig) {
        regularFilterConfig.enabled =
          filterConfig.enabled !== undefined
            ? filterConfig.enabled
            : routeFiltersConfig.regularFiltersEnabled
        regularFilterConfig.multiselectDisabled =
          regularFilterConfig.multiselectDisabled !== undefined
            ? regularFilterConfig.multiselectDisabled
            : routeFiltersConfig.multiselectDisabled
      }
      if (regularFilterConfig.enabled && regularFilterConfig.values.length < 2) {
        regularFilterConfig.enabled = false
      }
    }

    return filterConfig
  }

  private static mergeInstanceAndRouteDateFilterConfig(
    instanceFilterConfig: InstanceDateFilterConfig,
    routeFilterConfig: DynamicDateFilterConfig,
  ): DateFilterConfig {
    let { minDateInMillis, maxDateInMillis, maxPeriodInMillis } = instanceFilterConfig
    if (routeFilterConfig) {
      const routeMinDateInMillis = routeFilterConfig.minDateInMillis
      if (routeMinDateInMillis) {
        minDateInMillis = minDateInMillis
          ? Math.max(minDateInMillis, routeMinDateInMillis)
          : routeMinDateInMillis
      }
      const routeMaxDateInMillis = routeFilterConfig.maxDateInMillis
      if (routeMaxDateInMillis) {
        maxDateInMillis = maxDateInMillis
          ? Math.min(maxDateInMillis, routeMaxDateInMillis)
          : routeMaxDateInMillis
      }
      const routeMaxPeriodInMillis = routeFilterConfig.maxPeriodInMillis
      if (routeMaxPeriodInMillis) {
        maxPeriodInMillis = maxPeriodInMillis
          ? Math.min(maxPeriodInMillis, routeMaxPeriodInMillis)
          : routeMaxPeriodInMillis
      }
    }
    return {
      ...instanceFilterConfig,
      ...routeFilterConfig,
      minDateInMillis,
      maxDateInMillis,
      maxPeriodInMillis,
    }
  }

  private static mergeInstanceAndRouteDateRangeFilterConfig(
    instanceFilterConfig: InstanceDateFilterConfig,
    routeFilterConfig: DynamicDateRangeFilterConfig,
  ): DateRangeFilterConfig {
    return {
      ...instanceFilterConfig,
      ...routeFilterConfig,
    }
  }

  private static mergeInstanceAndRouteRealtimeFilterConfig(
    instanceFilterConfig: InstanceRealtimeFilterConfig,
    routeFilterConfig: DynamicRealtimeFilterConfig,
  ): RealtimeFilterConfig {
    let intervalsInMinutes = routeFilterConfig ? routeFilterConfig.intervalsInMinutes : []
    if (instanceFilterConfig.maxPeriodInMinutes) {
      intervalsInMinutes = intervalsInMinutes.filter(
        intervalInMinutes => intervalInMinutes <= instanceFilterConfig.maxPeriodInMinutes,
      )
    }
    return {
      ...instanceFilterConfig,
      ...routeFilterConfig,
      intervalsInMinutes,
    }
  }

  private static mergeInstanceAndRouteRegularFilterConfig(
    instanceFilterConfig: InstanceRegularFilterConfig,
    routeFilterConfig: DynamicRegularFilterConfig,
  ): RegularFilterConfig {
    const multiselectDisabled =
      instanceFilterConfig.multiselectDisabled ||
      (!!routeFilterConfig && routeFilterConfig.multiselectDisabled) ||
      false
    return {
      ...instanceFilterConfig,
      ...routeFilterConfig,
      multiselectDisabled,
    }
  }

  private static getFilterSelectedValues(
    context: ContextState,
    route: AppRoute,
    filterConfig: FilterConfig,
    options?: GetFiltersFromContextAndRouteOptions,
  ): FilterSelectedValues {
    if (filterConfig.type === FilterTypeEnum.REGULAR && options?.toolStateKey === 'tools') {
      return FiltersUtils.getRegularFilterSelectedValuesForToolList(
        route,
        filterConfig as RegularFilterConfig,
        options.persistedSelectedFiltersValues,
      )
    }

    const filter = context.filters[filterConfig.id]
    const filterContextValues = filter ? filter.selectedValues : undefined
    if (filterContextValues) {
      return filterContextValues
    }

    const filterUrlValues = route.query[
      `${ROUTER_CONSTS.query.filterPrefix}${filterConfig.id}`
    ] as string

    return {
      [FilterTypeEnum.DATE]: () => FiltersUtils.getDateFilterSelectedValuesFromUrl(filterUrlValues),
      [FilterTypeEnum.DATE_RANGE]: () =>
        FiltersUtils.getDateRangeFilterSelectedValuesFromUrl(filterUrlValues),
      [FilterTypeEnum.REALTIME]: () =>
        FiltersUtils.getRealtimeFilterSelectedValuesFromUrl(filterUrlValues),
      [FilterTypeEnum.REGULAR]: () =>
        FiltersUtils.getRegularFilterSelectedValuesFromUrl(filterUrlValues),
    }[filterConfig.type]()
  }

  private static getRegularFilterSelectedValuesForToolList(
    route: AppRoute,
    filterConfig: RegularFilterConfig,
    persistedSelectedFiltersValues?: Dictionary<FilterSelectedValues>,
  ): RegularFilterSelectedValues {
    const queryFilterId = `${ROUTER_CONSTS.query.filterPrefix}${filterConfig.id}`

    if (queryFilterId in route.query) {
      return FiltersUtils.getRegularFilterSelectedValuesFromUrl(
        route.query[queryFilterId] as string,
      )
    }

    return (
      (persistedSelectedFiltersValues?.[filterConfig.id] as
        | RegularFilterSelectedValues
        | undefined) ?? FiltersUtils.getListFilterDefaultSelectedValues(filterConfig)
    )
  }

  private static getDateFilterSelectedValuesFromUrl(
    filterUrlValues: string,
  ): DateFilterSelectedValues {
    const filterValues = (!!filterUrlValues && filterUrlValues.split(',')) || undefined
    const startDateInMillis = (!!filterValues && Date.parse(filterValues[0])) || undefined
    const endDateInMillis = (!!filterValues && Date.parse(filterValues[1])) || undefined
    return [startDateInMillis, endDateInMillis]
  }

  private static getDateRangeFilterSelectedValuesFromUrl(
    filterUrlValues: string,
  ): DateRangeFilterSelectedValues {
    if (!filterUrlValues) {
      return [undefined, undefined]
    }

    const [startDate, endDate] = filterUrlValues.split(',')
    return [startDate || undefined, endDate || undefined]
  }

  private static getRealtimeFilterSelectedValuesFromUrl(
    filterUrlValues: string,
  ): RealtimeFilterSelectedValues {
    return filterUrlValues ? Number(filterUrlValues) : undefined
  }

  private static getRegularFilterSelectedValuesFromUrl(
    filterUrlValues: string,
  ): RegularFilterSelectedValues {
    return filterUrlValues ? filterUrlValues.split(',') : []
  }

  private static setFiltersToBeValid(filters: Dictionary<Filter>): Dictionary<Filter> {
    Object.values(filters).forEach((filter: Filter) => {
      filters[filter.config.id] = {
        [FilterTypeEnum.DATE]: () => FiltersUtils.setDateFilterToBeValid(filter as DateFilter),
        [FilterTypeEnum.DATE_RANGE]: () => filter,
        [FilterTypeEnum.REALTIME]: () =>
          FiltersUtils.setRealtimeFilterToBeValid(filter as RealtimeFilter),
        [FilterTypeEnum.REGULAR]: () =>
          FiltersUtils.setRegularFilterToBeValid(filter as RegularFilter),
      }[filter.config.type]()
    })
    return filters
  }

  private static setDateFilterToBeValid(filter: DateFilter): DateFilter {
    if (
      filter.config.enabled &&
      filter.config.mandatory &&
      (!filter.selectedValues[0] || !filter.selectedValues[1])
    ) {
      const now = new Date(Date.now())
      const defaultStartDateInMillis = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() - 7,
      )
      const defaultEndDateInMillis = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
      )
      filter.selectedValues = [defaultStartDateInMillis, defaultEndDateInMillis]
    }
    return filter
  }

  private static setRealtimeFilterToBeValid(filter: RealtimeFilter): RealtimeFilter {
    if (filter.config.enabled) {
      if (
        !!filter.selectedValues &&
        !filter.config.intervalsInMinutes.includes(filter.selectedValues)
      ) {
        filter.selectedValues = undefined
      }
      if (filter.config.mandatory && !filter.selectedValues) {
        filter.selectedValues = filter.config.intervalsInMinutes[0]
      }
    }
    return filter
  }

  private static setRegularFilterToBeValid(filter: RegularFilter): RegularFilter {
    if (typeof filter.config.getAsyncValues === 'function' || !filter.config.enabled) {
      return filter
    }

    filter.selectedValues = filter.selectedValues.filter((selectedValue: string) =>
      filter.config.values.some(
        (valueConfig: RegularFilterValueConfig) => valueConfig.id === selectedValue,
      ),
    )

    if (filter.config.multiselectDisabled && filter.selectedValues.length > 1) {
      filter.selectedValues = []
    }

    return filter
  }

  private static getDateFilterUrlValues(filter: DateFilter): string {
    if (!!filter.selectedValues[0] || !!filter.selectedValues[1]) {
      return filter.selectedValues
        .map((dateInMillis: number) => {
          if (dateInMillis) {
            return `${new Date(dateInMillis).toISOString().substr(0, 16)}Z`
          }
        })
        .join()
    }
  }

  private static getDateRangeFilterUrlValues(filter: DateRangeFilter): string {
    if (!!filter.selectedValues[0] || !!filter.selectedValues[1]) {
      return filter.selectedValues.map((dateString: string) => dateString || '').join()
    }
  }

  private static getRealtimeFilterUrlValues(filter: RealtimeFilter): string {
    if (filter.selectedValues) {
      return filter.selectedValues.toString()
    }
  }

  private static getRegularFilterUrlValues(filter: RegularFilter): string {
    if (filter.selectedValues.length) {
      return filter.selectedValues.join()
    }
  }

  static getListFilterDefaultSelectedValues(
    config: RegularFilterConfig,
  ): RegularFilterSelectedValues {
    return config.id === FilterIdEnum.ENDPOINT || typeof config.getAsyncValues === 'function'
      ? []
      : FiltersUtils.getSelectedValuesFromDefaultValueId(config)
  }

  static getDefaultSelectedValues(
    filter: RegularFilter,
    setAtLeastOneSelectedValue = false,
  ): Array<string> {
    if (!filter.config.mandatory || filter.selectedValues.length) {
      return []
    }

    const defaultValueSelectedValues = FiltersUtils.getSelectedValuesFromDefaultValueId(
      filter.config,
    )

    if (defaultValueSelectedValues.length) {
      return defaultValueSelectedValues
    }

    if (
      filter.config.values.length === 1 ||
      (filter.config.values.length > 0 && setAtLeastOneSelectedValue)
    ) {
      return [filter.config.values[0].id]
    }

    return []
  }

  private static getSelectedValuesFromDefaultValueId(
    config: RegularFilterConfig,
  ): RegularFilterSelectedValues {
    const { defaultValueId, values, multiselectDisabled } = config

    if (!defaultValueId) {
      return []
    }

    if (defaultValueId === ALL_FILTERS_CODE && !multiselectDisabled) {
      return values.map(({ id }) => id)
    }

    return values.some(({ id }) => id === defaultValueId) ? [defaultValueId] : []
  }

  static async getFiltersByInstanceAndFeature(
    instance: Instance,
    featureId: FeatureIdEnum,
    domain: DomainEnum,
  ): Promise<RouteFiltersConfig> {
    let config: Record<string, DynamicFilterConfig> | undefined
    try {
      const store = CONTAINER.get<Store<unknown>>(TYPES.Store)
      const toolConfig = (await store.dispatch(
        `${TOOLING_CONSTS.moduleName}/${TOOLING_CONSTS.actions.loadToolConfig}`,
        { tool: featureId, domain },
      )) as PivotableToolConfig
      config = toolConfig.pivots.reduce<Record<string, DynamicFilterConfig>>((result, pivot) => {
        const filterConfig = FiltersUtils.findConfig(Object.values(instance.filters), {
          toolingId: pivot.name,
        })
        if (filterConfig) {
          result[filterConfig.id] = {
            enabled: true,
            multiselectDisabled: featureId !== FeatureIdEnum.EQUALIZE,
          }
        }
        return result
      }, {})
    } catch {}
    return {
      config,
      extra: {
        [ENABLED_FILTER_CONFIG.id]: ENABLED_FILTER_CONFIG,
      },
    }
  }

  static filterByIds(filters: Record<string, Filter>, ids: string[]): Record<string, Filter> {
    return Object.fromEntries(
      Object.entries(filters).filter(
        ([, filter]: [string, RegularFilter]) =>
          ids.includes(filter.config.id) || ids.includes(filter.config.taggingId),
      ),
    )
  }
}
